import { useContext } from 'react';
import {
    MutationFunction,
    useMutation,
    UseMutationOptions,
    useQuery,
    UseQueryOptions,
} from '@tanstack/react-query';
import useRestService from './RestService';
import { useAPI } from '../lib/hooks';
import { AppContext } from '../sporttia/AppContext';
import {
    PostLogin200,
    PostLoginBody200,
    PostMyScPupil200,
    PostMyScPupilBody,
    PostUserValidate,
    PostValidate200,
} from '../types/api/paths/User';
import { User } from '../types/api/models';

const resource = '/users';

const getGetUserQueryKey = (id?: number) => [`${resource}/${id}`];

/**
 * Hook to access all 'users' resources.
 */
export default function useUsersService() {
    const { migrationApis } = useContext(AppContext)!;
    const [request] = useAPI(migrationApis);
    const services = useRestService(resource);

    /**
     * GET /users/:id/agendas request.
     * @param id User ID.
     * @param params Resource parameters to be sent with the request.
     * @returns {Promise<unknown>|*} Return request promise.
     */
    function getUsersAgendas<T>(id: number, params: Record<string, unknown>) {
        return request<T>('GET', `${resource}/${id}/agendas`, params);
    }

    /**
     * POST /tpvs/:id/purchase
     */
    function usePost2FACode(
        mutationOptions?: UseMutationOptions<
            PostLogin200,
            unknown,
            { params?: PostLoginBody200 }
        >,
    ) {
        const mutationFn: MutationFunction<
            PostLogin200,
            { params: PostLoginBody200 }
        > = ({ params }: { params: PostLoginBody200 }) =>
            request('POST', `/login`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * POST /my/scs/:scId/pupil request.
     */
    function useCreateMyScPupil(
        mutationOptions?: UseMutationOptions<
            PostMyScPupil200,
            unknown,
            { scId: number; params: PostMyScPupilBody }
        >,
    ) {
        const mutationFn: MutationFunction<
            PostMyScPupil200,
            { scId: number; params: PostMyScPupilBody }
        > = ({ scId, params }: any) =>
            request('POST', `/my/scs/${scId}/pupils`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * POST /users/validate
     */
    function useValidateUser(
        mutationOptions?: UseMutationOptions<
            PostValidate200,
            unknown,
            { params?: PostUserValidate }
        >,
    ) {
        const mutationFn: MutationFunction<
            PostValidate200,
            { params: PostUserValidate }
        > = ({ params }: { params: PostUserValidate }) =>
            request('POST', `${resource}/validate`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * POST /users/{id}/confirm
     */
    function useConfirmUser(
        mutationOptions?: UseMutationOptions<
            void,
            unknown,
            {
                userId: number;
                params: {
                    signature: string;
                };
            }
        >,
    ) {
        const mutationFn: MutationFunction<
            void,
            { userId: number; params: { signature: string } }
        > = ({
            userId,
            params,
        }: {
            userId: number;
            params: { signature: string };
        }) => request('PUT', `${resource}/${userId}/confirm`, params);

        return useMutation({ mutationFn, ...mutationOptions });
    }

    /**
     * GET /users/{id} request.
     */
    function useGetUser<TData = { user: User }>(
        id?: number,
        queryOptions?: UseQueryOptions<{ user: User }, unknown, TData>,
    ) {
        const queryKey = queryOptions?.queryKey ?? getGetUserQueryKey(id);

        const queryFn = () =>
            request<{ user: User }>('GET', `${resource}/${id}`);

        const query = useQuery({ queryKey, queryFn, ...queryOptions });

        return {
            ...query,
            queryKey,
        };
    }

    return {
        ...services,
        getUsersAgendas,
        usePost2FACode,
        useCreateMyScPupil,
        useValidateUser,
        useConfirmUser,
        useGetUser,
    };
}
